import type { ReactNode } from 'react';
import type { AccountTransaction } from '@hero/schema';
import type { UseInfinitePaginationReturn } from '@hero/api-hooks';
import { useTranslation } from 'react-i18next';
import { SharedList } from '../../../app/List/List';
import { AccountTransactionCard } from '../Card/AccountTransactionCard';

export interface AccountTransactionListProps {
  query: Pick<
    UseInfinitePaginationReturn<AccountTransaction>,
    'data' | 'isLoading' | 'onLoadMore' | 'hasNextPage' | 'isFetchingNextPage'
  >;
  renderTransaction?: (transaction: AccountTransaction) => ReactNode;
  className?: string;
}

/**
 * Card-list rendering of an `AccountTransaction` infinite query.
 * Mirrors `TransactionList` 1:1 — host owns the query, this owns
 * the layout + empty state + pagination button.
 */
export function AccountTransactionList({
  query,
  renderTransaction,
  className,
}: AccountTransactionListProps) {
  const { t } = useTranslation(['m-account-transaction']);

  return (
    <div className="w-full max-w-2xl mx-auto">
      <SharedList<AccountTransaction>
        query={query}
        emptyMessage={t('m-account-transaction:list.no_transactions', {
          defaultValue: 'No transactions yet.',
        })}
        className={className ?? 'gap-3 p-4'}
        renderItem={transaction =>
          renderTransaction ? (
            renderTransaction(transaction)
          ) : (
            <AccountTransactionCard transaction={transaction} />
          )
        }
      />
    </div>
  );
}
